/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
/* eslint-disable max-lines */
import moment from "moment";
import { FormattedMessage, IntlShape } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Column } from "react-table";

import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import Badge from "components/atoms/Badge/Badge";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import DuplicateShipment from "components/molecules/DuplicateShipment/DuplicateShipment";
import { formatDatetime, formatWeight, roundTo } from "helpers/commonHelpers";
import { ReactComponent as EditIcon } from "assets/images/icons/SubmitIcon.svg";
import Cookies from "js-cookie";
import { startEditQuoteRequest } from "api";
import errorToast from "helpers/errorToast";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import OtherStatus from "components/organisms/OtherStatus/OtherStatus";
import { QuoteStatusColors } from "constants/";
import { DateTimeFormat } from "helpers/dateTimeHelper";

function formatDate(date: any) {
  // handle case when user does not select any date from pickup or delivery date and return -
  if (date === " ") return " -";
  if (date === "ASAP") return "ASAP";

  // handle draft save for pickup date and delivery date
  if (date && date !== " ") {
    const splitDate = date.split(" ");
    // after split index 0 and index 1 if same date and return ASAP
    if (splitDate[0] === splitDate[1]) return "ASAP";
  }
  return moment(date).format("DD MMM YYYY");
}

function formatDatetimeWithLocale(datetime: any, intl: IntlShape, customFormat = DateTimeFormat.shortDateTimeFormat) {
  if (datetime === null || datetime === " ") return "-";
  if (datetime?.includes("ASAP")) {
    return intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyOption.asap",
      defaultMessage: "ASAP",
    });
  }
  return moment(datetime).format(customFormat);
}

const getQuoteListingColumns = (
  intl: IntlShape,
  status: string,
  paymentLocationsHashMap: Record<string, any>,
  canCreateQuoteRequest: boolean,
  navigate: ReturnType<typeof useNavigate>,
  prefferedTimezone: string,
  can_consolidate_orders: boolean,
  isV2 = false,
) => {
  let columns: Column[] = [
    {
      id: "shipment",
      Header: intl.formatMessage({
        id: ["draft", "system_draft"].includes(status) ? "general.draft" : "general.shipment",
        defaultMessage: ["draft", "system_draft"].includes(status) ? "Draft" : "Shipment",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "quote_request_reference",
      sortable: true,
      Cell: ({ value, row }: any) => (
        <>
          <p className="font-b4 mb-1">{value || "-"}</p>
          <span>{row?.original?.quote_request_title || "-"}</span>
        </>
      ),
    },
    ...(can_consolidate_orders
      ? [
          {
            id: "type",
            Header: intl.formatMessage({
              id: "general.type",
              defaultMessage: "Type",
            }),
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            accessor: "shipment_type",
            sortable: true,
            Cell: ({ value }: any) => (
              <span className="font-b5">
                {value === "2"
                  ? intl.formatMessage({ id: "quote.listing.typeConsolidated", defaultMessage: "Consolidated" })
                  : intl.formatMessage({ id: "quote.listing.typeSingle", defaultMessage: "Single" })}
              </span>
            ),
          },
        ]
      : []),
    {
      Header: intl.formatMessage({
        id: "general.created",
        defaultMessage: "Created",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "payment_location",
      sortable: true,
      Cell: ({ value, row }: any) => (
        <>
          <p className="mb-0">
            {status === "draft" || status === "system_draft" ? paymentLocationsHashMap[value]?.label : value}
          </p>
          <span className="text-primary mt-2">
            {(status === "draft" || status === "system_draft") && typeof row.original?.created_by === "string" ? (
              row.original?.created_by
            ) : (
              <OverlayTrigger
                placement="top"
                delay={{ show: 50, hide: 50 }}
                overlay={<Tooltip id="button-tooltip">{row.original?.created_by?.email}</Tooltip>}
              >
                <span>{row.original?.created_by?.name}</span>
              </OverlayTrigger>
            )}
          </span>
        </>
      ),
    },
    ...(!["draft", "system_draft"].includes(status)
      ? [
          {
            Header: intl.formatMessage({
              id: "quote.listing.quotation",
              defaultMessage: "Quotation",
            }),
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            id: "quotations",
            accessor: "quotes_count",
            sortable: true,
            Cell: ({ value }: any) => <div>{value || "-"}</div>,
          },
        ]
      : []),
    {
      Header: intl.formatMessage({
        id: "shipments.freightModeTableHeader",
        defaultMessage: "Freight mode",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: isV2 ? "freight_modes" : "freight_modes_v2",
      Cell: ({ value }) => (
        <div>
          {value?.length > 0
            ? value.map((item: any, index: number) => {
                const Icon = getFreightModeIcon(item.name, item.type, true);
                return (
                  <span className="mr-2" key={index}>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="freight-mode">
                          <span className="font-weight-normal">{item.name}</span>
                        </Tooltip>
                      }
                    >
                      <Icon />
                    </OverlayTrigger>
                  </span>
                );
              })
            : "-"}
        </div>
      ),
    },
    {
      id: "route",
      Header: intl.formatMessage({
        id: "quote.listing.fromTo",
        defaultMessage: "From-to",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "pickup_city",
      sortable: true,
      Cell: ({ value, row }: any) => {
        if (!value && !row.original?.stop1_city) return <div className="mb-0">-</div>;
        const deliveryDate = row.original?.stop1_date;
        const pickupDate = row.original?.pickup_date;
        const pickupCity = row.original?.pickup_city;
        const deliveryCity = row.original?.stop1_city;
        return (
          <>
            <div>
              <div className="d-flex justify-content-between w-190-px">
                <div className="font-b5 overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{pickupCity}</span>
                  <div className="text-primary text-truncate">
                    {Array.isArray(pickupDate) ? (
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 50, hide: 50 }}
                        overlay={
                          <Tooltip id="button-tooltip">
                            {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                          </Tooltip>
                        }
                      >
                        <span>
                          {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                        </span>
                      </OverlayTrigger>
                    ) : (
                      formatDatetime(pickupDate)
                    )}
                  </div>
                </div>
                <div className="font-b5 text-right overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{deliveryCity}</span>
                  <div className="font-b5 text-primary">
                    {formatDatetimeWithLocale(deliveryDate, intl, DateTimeFormat.shortDateTimeFormat)}
                  </div>
                </div>
              </div>
            </div>
            {Cookies.get("listing_sizeperpage") !== "100" && (
              <div className="d-flex justify-content-between position-relative w-190-px">
                <div
                  style={{
                    width: 190,
                    height: 2,
                    borderBottom: `2px dotted black`,
                    position: "absolute",
                    top: 3,
                  }}
                />
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>

                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        );
      },
    },
    ...(!["draft", "system_draft"].includes(status)
      ? [
          {
            id: "status",
            Header: intl.formatMessage({
              id: "general.status",
              defaultMessage: "Status",
            }),
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            accessor: "display_status",
            sortable: true,
            Cell: ({ value: cellValue, row }: any) => {
              if (!cellValue) return;
              const sortedStatusArr = cellValue.sort((a: any, b: any) => {
                const priority = ["Approval pending", "Awaiting action", "Closed for quotations", "Open for quotations"];
                return priority.indexOf(a.value) - priority.indexOf(b.value);
              });
              const { value, label } = sortedStatusArr[0] ?? {};
              let badgeType = QuoteStatusColors[value] || "secondary";
              if (value === "Open for quotations") {
                const prefferedTimezoneCurrentDateTime = moment(
                  new Date().toLocaleString("en-US", { timeZone: prefferedTimezone }),
                  "M/DD/YYYY, hh:mm:ss A",
                ).format("YYYY-MM-DD HH:mm:ss");

                const remainingTimeInSeconds = moment
                  .duration(
                    moment(row.original?.confirmation_datetime, "YYYY-MM-DD HH:mm:ss").diff(
                      moment(prefferedTimezoneCurrentDateTime),
                    ),
                  )
                  .asSeconds();

                if (remainingTimeInSeconds > 0 && remainingTimeInSeconds < 300) {
                  badgeType = "danger";
                } else if (remainingTimeInSeconds > 0 && remainingTimeInSeconds < 3600) {
                  badgeType = "warning";
                }
              }

              return (
                <div className="d-flex flex-column align-items-start">
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                      {value && label && <Badge type={badgeType} label={label} />}
                      {sortedStatusArr.length > 1 && (
                        <div>
                          <OtherStatus statusArr={sortedStatusArr} />
                        </div>
                      )}
                    </div>
                    {["Open for quotations", "Approval pending"].includes(value) &&
                      row.original?.confirmation_timer_remaining_time_display && (
                        <div className="text-primary font-b3 mt-1">
                          {row.original?.confirmation_timer_remaining_time_display}{" "}
                          <FormattedMessage id="quote.listing.quoteRemaining" defaultMessage="remaining" />
                        </div>
                      )}
                  </div>
                </div>
              );
            },
          } as Column,
        ]
      : []),
    ...(!["draft", "system_draft"].includes(status)
      ? ([
          {
            Header: getLabelWithTranslationFallback("Confirmation time", {
              translation: intl.formatMessage({
                id: "new-request.quote-confirmation.schedule.confirmationDate",
                defaultMessage: "Confirmation date",
              }),
              returnAsString: true,
              withoutInfo: true,
            }),
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            sortable: true,
            accessor: "confirmation_datetime",
            Cell: ({ value }) => {
              const time = moment(value).format("HH:mm");
              return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
            },
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.createdDate", defaultMessage: "Created date" }),
            sortable: true,
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            accessor: "created",
            Cell: ({ value }) => {
              const time = moment(value).format("HH:mm");
              return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
            },
          },
          // {
          //   Header: intl.formatMessage({ id: "tableHeaders.modified", defaultMessage: "Modified" }),
          //   sortable: true,
          //   id: "modified",
          //   accessor: "modified",
          //   Cell: ({ row }) => {
          //     const time = moment(row.original?.modified).format("HH:mm");
          //     return <div>{row.original?.modified ? `${formatDate(row.original?.modified)} - ${time}` : "-"}</div>;
          //   },
          // },
          {
            Header: intl.formatMessage({ id: "new-request.packing-list.dangerousGoods", defaultMessage: "Dangerous goods" }),
            accessor: "dangerous_goods",
            group: intl.formatMessage({
              id: "columns.goodsInformation",
              defaultMessage: "Goods information",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.totalWeightKg", defaultMessage: "Total weight (kg)" }),
            accessor: "total_weight_kg",
            group: intl.formatMessage({
              id: "columns.goodsInformation",
              defaultMessage: "Goods information",
            }),
            Cell: ({ value }) => <div>{formatWeight(value, "kg")}</div>,
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.totalWeightLbs", defaultMessage: "Total weight (lbs)" }),
            accessor: "total_weight_lbs",
            group: intl.formatMessage({
              id: "columns.goodsInformation",
              defaultMessage: "Goods information",
            }),
            Cell: ({ value }) => <div>{formatWeight(value, "lbs")}</div>,
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.totalVolumeCbm", defaultMessage: "Total volume (cbm)" }),
            accessor: "total_volume_cbm",
            group: intl.formatMessage({
              id: "columns.goodsInformation",
              defaultMessage: "Goods information",
            }),
            Cell: ({ value }) => (
              <div>
                {value || value === 0 ? (
                  <span>
                    {roundTo(value, 4)}m<sup>3</sup>
                  </span>
                ) : (
                  "-"
                )}
              </div>
            ),
          },
          {
            Header: intl.formatMessage({
              id: "tableHeaders.expectedPickupDateTime",
              defaultMessage: "Expected pickup date time",
            }),
            accessor: "pickup_date",
            group: intl.formatMessage({
              id: "columns.schedule",
              defaultMessage: "Schedule",
            }),
            sortable: true,
            Cell: ({ value }) => {
              const time = moment(value).format("HH:mm");
              return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
            },
          },
          {
            Header: intl.formatMessage({
              id: "tableHeaders.expectedDeliveryDateTime",
              defaultMessage: "Expected delivery date time",
            }),
            accessor: "stop1_date",
            group: intl.formatMessage({
              id: "columns.schedule",
              defaultMessage: "Schedule",
            }),
            sortable: true,
            Cell: ({ value }) => {
              const time = moment(value).format("HH:mm");
              if (value === "ASAP") return <div>{value}</div>;
              return <div>{value ? `${formatDate(value)} - ${time}` : "-"}</div>;
            },
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.quotationsBooked", defaultMessage: "Quotations booked" }),
            accessor: "related_shipments_count",
            group: intl.formatMessage({
              id: "columns.schedule",
              defaultMessage: "Schedule",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.exportClearance", defaultMessage: "Export clearance" }),
            accessor: "export_clearance",
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "quote-request-detail.transitClearance", defaultMessage: "Transit clearance" }),
            accessor: "transit_clearance",
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "quote-request-detail.importClearance", defaultMessage: "Import clearance" }),
            accessor: "import_clearance",
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({
              id: "new-request.service.serviceRequirement",
              defaultMessage: "Service requirement",
            }),
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            accessor: "service_requirement",
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "new-request.service.incoterms", defaultMessage: "Incoterms" }),
            accessor: "incoterms",
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "general.insurance", defaultMessage: "Insurance" }),
            accessor: "insurance",
            group: intl.formatMessage({
              id: "columns.service",
              defaultMessage: "Service",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: getLabel("Custom field 01", "capitalize"),
            accessor: "custom_field_01",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: getLabel("Custom field 02", "capitalize"),
            accessor: "custom_field_02",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: getLabel("Reason code", "capitalize", "", false, true),
            accessor: "reason_code",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: getLabel("Cost Center / WBS / Project #", "capitalize", "", false, true),
            accessor: "cost_center",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: getLabel("Payment location", "capitalize-first"),
            id: "location",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            accessor: "payment_location",
            sortable: true,
            Cell: ({ value }) => <div>{value || "-"}</div>,
          },
          {
            Header: intl.formatMessage({ id: "tableHeaders.createdBy", defaultMessage: "Created by" }),
            accessor: "created_by",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }) => <div>{value.name || "-"}</div>,
          },
        ] as Column[])
      : []),
    ...(!["draft", "system_draft"].includes(status)
      ? [
          {
            Header: "",
            accessor: "quote_request_reference",
            actions: true,
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            disableDrag: true,
            Cell: ({ value: shipmentId, row }: any) => {
              const { status: shipmentStatus, display_status } = row.original;
              const autoCanceled = display_status?.find((status: any) => status.value === "Auto-Canceled")?.value;
              const canEdit = !["Canceled", "Delivered"].includes(shipmentStatus) && !autoCanceled && canCreateQuoteRequest;
              const canDuplicate = canCreateQuoteRequest;

              const [isActionInProgress, setIsActionInProgress] = useState(false);
              const quotesAvailable = row?.original?.quotes_count > 0;

              const handleEditClick = () => {
                setIsActionInProgress(true);
                startEditQuoteRequest(shipmentId)
                  .then((res) => {
                    const url = `/quote-request/${shipmentId}/edit?draft_id=${res.data.draft_reference}&quotesAvailable=${quotesAvailable}`;
                    navigate(url);
                  })
                  .catch((err) => errorToast(err))
                  .finally(() => setIsActionInProgress(false));
              };

              if (isActionInProgress) return <div className="spinner-border spinner-border-sm mr-2" role="status" />;
              return (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {canDuplicate && <DuplicateShipment type="quote" reference={shipmentId} />}
                  {canEdit && (
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 0 }}
                      overlay={
                        <Tooltip id="button-tooltip">
                          <FormattedMessage id="general.edit" defaultMessage="Edit" />
                        </Tooltip>
                      }
                    >
                      <EditIcon onClick={handleEditClick} className="ml-2" height="18" width="18" />
                    </OverlayTrigger>
                  )}
                </div>
              );
            },
          } as Column,
        ]
      : [
          {
            id: "last_modified",
            Header: intl.formatMessage({ id: "general.lastModified", defaultMessage: "Last Modified" }),
            accessor: "modified",
            group: intl.formatMessage({
              id: "columns.other",
              defaultMessage: "Other",
            }),
            Cell: ({ value }: any) => (
              <p className="mb-0">
                {moment.utc(value, "YYYY-MM-DD HH:mm:ss").fromNow(true)}{" "}
                <FormattedMessage id="general.ago" defaultMessage="ago" />
              </p>
            ),
          },
        ]),
  ];

  // if no custom label exists for the custom field, remove it from the column list
  if (getLabel("Custom field 01", "capitalize") === "Custom field 01") {
    columns = columns.filter((column) => column.Header !== "Custom field 01");
  }
  if (getLabel("Custom field 02", "capitalize") === "Custom field 02") {
    columns = columns.filter((column) => column.Header !== "Custom field 02");
  }

  return columns;
};

export default getQuoteListingColumns;
